import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import { handleErrors } from "@/shared/utils/forms";
import { useVerifyEmailMutation } from "@/redux/service";
import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";
import { EmailVerificationFormValues } from "./types";

const emailVerificationSchema = (
  t: TFunction,
): yup.ObjectSchema<EmailVerificationFormValues> =>
  yup.object().shape({
    token: yup
      .string()
      .typeError(t("auth.validation.invalidCode"))
      .required(t("auth.validation.codeRequired")),
  });

const EmailVerification = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [verifyEmail, { isLoading }] = useVerifyEmailMutation();
  const { t } = useTranslation();

  const form = useForm<EmailVerificationFormValues>({
    resolver: yupResolver(emailVerificationSchema(t)),
    mode: "onChange",
  });

  const sendCode = (token: string) => {
    verifyEmail({ token })
      .unwrap()
      .then((data) => {
        notification.success({
          message: t("auth.messages.verifyEmailSuccess"),
        });
        dispatch(
          setAuthenticationState({ isAuthenticated: true, user: data.user }),
        );
        navigate("/");
        window.localStorage.setItem(
          LocalStorageName,
          JSON.stringify({
            accessExpiration: data.accessExpiration,
            refreshExpiration: data.refreshExpiration,
            user: data.user,
          }),
        );
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  useEffect(() => {
    if (searchParams.has("token")) {
      const tokenParam = searchParams.get("token") as string;
      form.setValue("token", tokenParam);
      sendCode(tokenParam);
    }
  }, [searchParams]);

  const onSubmit = (values: EmailVerificationFormValues) => {
    sendCode(values.token);
  };

  return (
    <div className="flex flex-col gap-400">
      <h2 className="header-l-medium text-center">
        {t("auth.labels.emailVerify")}
      </h2>

      <div className="form-input-container">
        <FormFieldWrapper label={t("auth.labels.verificationCode")} required>
          <HookFormInput name="token" control={form.control} />
        </FormFieldWrapper>

        <div className="flex flex-col items-center">
          <BaseButton
            disabled={isLoading}
            isFullWidth
            onClick={form.handleSubmit(onSubmit)}
          >
            {t("auth.labels.submit")}
          </BaseButton>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
